<!-- CareManagement_PatientTable.vue — Custom patient worklist table -->
<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Table container -->
    <div class="flex-1 overflow-auto">
      <table :style="{ tableLayout: 'fixed', width: '100%', minWidth: tableMinWidth + 'px' }">
        <colgroup>
          <col style="width: 32px">
          <col v-for="col in columns" :key="'cg-' + col.key" :style="{ width: colWidths[col.key] + 'px' }">
        </colgroup>
        <thead class="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
          <tr>
            <th class="w-8 px-2 py-2.5">
              <input type="checkbox" :checked="allVisibleSelected" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500" @change="toggleSelectAll" />
            </th>
            <th
v-for="col in columns" :key="col.key"
              :class="[col.key === 'patient' ? 'px-4' : 'px-3', 'relative overflow-hidden text-left py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-700 transition-colors']"
              @click="toggleSort(col.key)"
            >
              {{ col.label }}
              <span v-if="sortKey === col.key" class="ml-0.5 text-primary-500">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              <div
                class="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-primary-300 active:bg-primary-500 z-10"
                @mousedown.stop="startColResize($event, col.key)"
                @click.stop
              ></div>
            </th>

          </tr>
        </thead>
        <tbody>
          <!-- Loading skeleton -->
          <template v-if="loading">
            <tr v-for="n in 12" :key="'skel-'+n" class="border-b border-gray-100">
              <td class="w-8 px-2 py-3"><div class="h-4 w-4 bg-gray-200 rounded animate-pulse"></div></td>
              <td class="px-4 py-3"><div class="h-4 bg-gray-200 rounded animate-pulse w-40"></div><div class="h-3 bg-gray-100 rounded animate-pulse w-24 mt-2"></div></td>
              <td class="px-3 py-3"><div class="h-4 bg-gray-200 rounded animate-pulse w-24"></div></td>
              <td class="px-3 py-3"><div class="h-4 bg-gray-200 rounded animate-pulse w-20"></div></td>
              <td class="px-3 py-3"><div class="h-5 bg-gray-200 rounded animate-pulse w-16"></div></td>
              <td class="px-3 py-3"><div class="h-5 bg-gray-200 rounded animate-pulse w-16"></div></td>
              <td class="px-3 py-3"><div class="h-4 bg-gray-200 rounded animate-pulse w-28"></div></td>
              <td class="px-3 py-3"><div class="h-4 bg-gray-200 rounded animate-pulse w-16"></div></td>
              <td class="px-3 py-3"><div class="h-4 bg-gray-200 rounded animate-pulse w-24"></div></td>
              <td class="px-3 py-3"><div class="h-5 bg-gray-200 rounded-full animate-pulse w-5"></div></td>
            </tr>
          </template>

          <template v-else>
          <tr
            v-for="patient in visiblePatients"
            :key="patient.entity_id"
            :class="[
              'border-b border-gray-100 cursor-pointer transition-colors',
              selectedEntityId === patient.entity_id
                ? 'bg-blue-50 hover:bg-blue-50'
                : 'hover:bg-gray-50'
            ]"
            @click="$emit('select-patient', patient)"
          >
            <!-- Checkbox -->
            <td class="w-8 px-2 py-3" @click.stop>
              <input type="checkbox" :checked="selectedEntityIds.has(patient.entity_id)" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500" @change="toggleSelect(patient.entity_id)" />
            </td>

            <!-- Patient -->
            <td class="px-4 py-3 whitespace-nowrap max-w-[130px]">
              <a
                :href="`/patients/${patient.patient_identifier}/drawer?entity=${patient.entity_id}`"
                target="_blank"
                :title="patient.patient_name"
                class="sensitive font-medium text-xs text-primary-700 hover:text-primary-900 hover:underline truncate block"
                @click.stop
              >{{ patient.patient_name }}</a>
              <div class="sensitive text-xs text-gray-500 mt-0.5">{{ patient.patient_identifier }}</div>
              <div v-if="patient.patient_dob || patient.bene_dob" class="sensitive text-xs text-gray-400">DOB: {{ formatDob(patient.patient_dob || patient.bene_dob) }}</div>
            </td>

            <!-- Practice / Provider -->
            <td class="px-3 py-3">
              <div class="sensitive flex flex-col justify-center leading-tight max-w-[100px]">
                <span class="text-xs text-gray-900 break-words sensitive" data-spoof-kind="practice">{{ titleCase(patient.practice_name) || '—' }}</span>
                <span v-if="patient.pcp_name && patient.pcp_name !== patient.practice_name" class="text-xs text-gray-500 break-words sensitive" data-spoof-kind="provider">{{ titleCase(patient.pcp_name) }}</span>
              </div>
            </td>

            <!-- Payer / Contract -->
            <td class="px-3 py-3">
              <div class="sensitive flex flex-col justify-center leading-tight max-w-[100px]">
                <span class="text-xs text-gray-900 break-words sensitive" data-spoof-kind="payer">{{ patientPayer(patient) }}</span>
                <span v-if="patientContract(patient)" class="text-xs text-gray-500 break-words sensitive" data-spoof-kind="contract">{{ patientContract(patient) }}</span>
              </div>
            </td>

            <!-- Index Event -->
            <td class="px-2 py-3 max-w-[140px]">
              <template v-if="activityInfo(patient).index">
                <div class="flex items-center gap-1">
                  <span class="text-xs font-medium" :class="episodeTypeColor(activityInfo(patient).index.type)">{{ activityInfo(patient).index.type }}</span>
                  <span class="text-xs text-gray-900">Discharge</span>
                </div>
                <div class="text-xs text-gray-500">{{ activityInfo(patient).index.date }} <span class="text-gray-400">({{ activityInfo(patient).index.ago }})</span></div>
                <div v-if="activityInfo(patient).index.disposition" class="text-xs text-gray-500 truncate" :title="activityInfo(patient).index.dispositionFull">
                  <FontAwesomeIcon :icon="['fas', 'arrow-right-from-bracket']" class="text-[8px] text-gray-400 mr-0.5" />{{ activityInfo(patient).index.disposition }}
                </div>
                <div v-if="activityInfo(patient).index.facility" class="text-xs text-gray-500 truncate sensitive" data-spoof-kind="facility" :title="activityInfo(patient).index.facility">
                  <FontAwesomeIcon :icon="['fas', 'hospital']" class="text-[8px] text-gray-400 mr-0.5" />{{ activityInfo(patient).index.facility }}
                </div>
              </template>
            </td>

            <!-- Latest Activity -->
            <td class="px-2 py-3 max-w-[140px]">
              <template v-if="activityInfo(patient).latest">
                <div class="flex items-center gap-1">
                  <span class="text-xs font-medium" :class="episodeTypeColor(activityInfo(patient).latest.type)">{{ activityInfo(patient).latest.type }}</span>
                  <span class="text-xs text-gray-900">{{ activityInfo(patient).latest.direction }}</span>
                </div>
                <div class="text-xs text-gray-500">{{ activityInfo(patient).latest.date }} <span class="text-gray-400">({{ activityInfo(patient).latest.ago }})</span></div>
                <div v-if="activityInfo(patient).latest.disposition" class="text-xs text-gray-500 truncate" :title="activityInfo(patient).latest.dispositionFull">
                  <FontAwesomeIcon :icon="['fas', 'arrow-right-from-bracket']" class="text-[8px] text-gray-400 mr-0.5" />{{ activityInfo(patient).latest.disposition }}
                </div>
                <div v-if="activityInfo(patient).latest.facility" class="text-xs text-gray-500 truncate sensitive" data-spoof-kind="facility" :title="activityInfo(patient).latest.facility">
                  <FontAwesomeIcon :icon="['fas', 'hospital']" class="text-[8px] text-gray-400 mr-0.5" />{{ activityInfo(patient).latest.facility }}
                </div>
              </template>
              <span v-else class="text-xs text-gray-300">—</span>
            </td>

            <!-- Risk/Acuity -->
            <td class="px-3 py-3 whitespace-nowrap">
              <RiskAcuityBadge :patient="patient" />
            </td>



            <!-- Programs: ALL currently-active programs for the patient,
                 independent of the stage/program/owner task filters (a patient
                 stays "in" a program even when its tasks are filtered out of
                 view). Backed by the filter-independent all_programs field;
                 falls back to enrolled_programs if absent. -->
            <td class="px-3 py-3 whitespace-nowrap">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="prog in (patient.all_programs || patient.enrolled_programs)"
                  :key="prog.id"
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border"
                  :style="{
                    backgroundColor: (prog.uicolor || '#10b981') + '15',
                    color: prog.uicolor || '#15803d',
                    borderColor: (prog.uicolor || '#10b981') + '40'
                  }"
                  :title="prog.name"
                >
                  {{ programAbbrev(prog.name) }}
                </span>
              </div>
            </td>

            <!-- Stage -->
            <td class="px-3 py-3 overflow-hidden">
              <div v-if="groupedTasks(patient).length" class="flex flex-col gap-1.5">
                <div v-for="group in groupedTasks(patient)" :key="group.id" class="flex items-center gap-1.5 flex-wrap">
                  <span
                    class="inline-flex items-center gap-1 pl-1.5 pr-1 py-1 rounded text-xs font-medium border transition-colors max-w-full"
                    :style="stageStyle(group, patient)"
                    :title="group.task_type + (group.call_attempts ? ` · ${group.call_attempts} call attempt${group.call_attempts === 1 ? '' : 's'}` : '') + ` · ${statusLabel(group)}`"
                  >
                    <!-- Status dot: green = completed/closed, hollow = not started,
                         yellow = 1-2 attempts, red = 3+ -->
                    <span
                      class="shrink-0 w-2 h-2 rounded-full border"
                      :class="statusDotClass(group)"
                    ></span>
                    <span
                      class="min-w-0 truncate cursor-pointer"
                      @click.stop="$emit('open-task', { patient, task: group })"
                    >{{ stageLabel(group) }}</span>
                    <button
                      class="shrink-0 ml-0.5 w-4 h-4 inline-flex items-center justify-center rounded hover:bg-black/10 transition-colors"
                      :title="'Launch clinical engagement'"
                      @click.stop="launchClinicalEngagement(group)"
                    >
                      <FontAwesomeIcon :icon="['fas', 'phone']" class="text-[9px]" />
                    </button>
                  </span>

                  <span
                    v-if="group.status_id === 4"
                    class="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-700"
                  >Snoozed</span>
                </div>
              </div>
              <span v-else class="text-xs text-gray-400">—</span>
            </td>

            <!-- Due Date -->
            <td class="px-3 py-3 whitespace-nowrap">
              <template v-if="earliestDueDate(patient)">
                <div class="text-xs font-medium" :class="dueDateClass({ due_date: earliestDueDate(patient) })">
                  {{ formatDueLabel({ due_date: earliestDueDate(patient) }) }}
                </div>
                <div class="text-[10px] text-gray-400">
                  {{ new Date(earliestDueDate(patient) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }}
                  <span v-if="earliestDueTime(patient)" class="ml-1">· {{ formatDueTime(earliestDueTime(patient), earliestDueDate(patient)) }}</span>
                </div>
              </template>
              <span v-else class="text-xs text-gray-300">—</span>
            </td>

            <!-- Assigned To -->
            <td class="px-3 py-3 whitespace-nowrap">
              <div class="flex items-center -space-x-1.5">
                <template v-if="patientOwners(patient).length">
                  <span
                    v-for="owner in patientOwners(patient).slice(0, 4)"
                    :key="owner.id"
                    class="sensitive inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 border-white"
                    data-demo-blur
                    :class="ownerBadgeColor(owner.role)"
                    :title="[owner.name, owner.role].filter(Boolean).join(' · ')"
                  >{{ ownerInitials(owner.name) }}</span>
                  <span
                    v-if="patientOwners(patient).length > 4"
                    class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-600 text-xs font-medium border-2 border-white"
                  >+{{ patientOwners(patient).length - 4 }}</span>
                </template>
                <span
                  v-else
                  class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 border-2 border-white"
                  title="Unassigned"
                >
                  <svg class="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                  </svg>
                </span>
              </div>
            </td>

            <!-- Work -->
            <td class="px-3 py-3 whitespace-nowrap">
              <div v-if="(patient.work_completed || []).length" class="flex items-center -space-x-0.5">
                <span
                  v-if="(patient.work_completed || []).length > 3"
                  class="text-[10px] text-gray-400 mr-1"
                >{{ (patient.work_completed || []).length - 3 }}+</span>
                <div
                  v-for="(work, idx) in (patient.work_completed || []).slice(-3)"
                  :key="work.id || idx"
                  class="w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-white border-2 border-white"
                  style="font-size: 11px;"
                  :class="workDotColor(work.work_type_name)"
                  :title="workTooltip(work)"
                ><FontAwesomeIcon :icon="workIcon(work.work_type_name)" /></div>
              </div>
              <span v-else class="text-xs text-gray-300">—</span>
            </td>


          </tr>

          <!-- Empty state -->
          <tr v-if="!sortedPatients.length">
            <td colspan="11" class="px-4 py-12 text-center text-sm text-gray-500">
              No patients match the current filters.
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Selection Action Bar -->
    <div v-if="selectedEntityIds.size > 0" class="px-4 py-2 border-t border-gray-200 bg-primary-50 shrink-0 flex items-center gap-3">
      <span class="text-xs font-semibold text-primary-700">{{ selectedEntityIds.size }} selected</span>
      <button
        class="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors"
        @click="openReassignModal"
      >Reassign</button>
      <button class="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700" @click="clearSelection">Clear</button>
    </div>

    <!-- Reassign Modal -->
    <Teleport to="body">
      <div v-if="showReassignModal" class="fixed inset-0 bg-gray-800/60 flex items-center justify-center z-50" @click.self="showReassignModal = false">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
          <div class="px-5 py-4 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-900">Reassign {{ selectedEntityIds.size }} patient{{ selectedEntityIds.size !== 1 ? 's' : '' }}</h3>
          </div>
          <div class="px-5 py-4 space-y-4">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Program</label>
              <select v-model="reassignMetricSlug" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                <option value="">All programs</option>
                <option v-for="p in selectedProgramOptions" :key="p.slug" :value="p.slug">{{ p.label }}</option>
              </select>
            </div>

            <!-- Stage selection: distinct task types among the selected patients' active tasks -->
            <div v-if="reassignStageOptions.length">
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-medium text-gray-600">Stages</label>
                <button class="text-[11px] text-primary-600 hover:text-primary-700" @click="toggleAllStages">
                  {{ allStagesSelected ? 'Clear all' : 'Select all' }}
                </button>
              </div>
              <div class="max-h-40 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                <label
                  v-for="s in reassignStageOptions"
                  :key="s.key"
                  class="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <input type="checkbox" :checked="reassignStageKeys.has(s.key)" class="rounded border-gray-300 text-primary-600 focus:ring-primary-500" @change="toggleStage(s.key)" />
                  <span class="flex-1">{{ s.label }}</span>
                  <span class="text-gray-400">{{ s.count }}</span>
                </label>
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Assign To</label>
              <select v-model="reassignMemberId" class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                <option :value="null">Select care manager...</option>
                <option v-for="m in reassignMembers" :key="m.id" :value="m.id">{{ m.name }}</option>
              </select>
            </div>

            <!-- SCOPE — DELIBERATELY UNSET. There is no correct default here:
                 "cover this stage" and "hand over the patient" are different
                 intentions with opposite consequences, and guessing either way
                 produces a silent error. Covering a call must not permanently
                 move a case; moving a case must not have to be done twice. So
                 the user states which one they mean and Reassign stays disabled
                 until they do. -->
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">What are you reassigning?</label>
              <div class="border border-gray-200 rounded-md divide-y divide-gray-100">
                <label class="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50">
                  <input
v-model="reassignScope" type="radio" value="stages_only"
                    class="mt-0.5 border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span class="text-xs">
                    <span class="font-medium text-gray-800">These stages only</span>
                    <span class="block text-gray-500">Covering the work. The next stage still goes to the current case owner.</span>
                  </span>
                </label>
                <label class="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50">
                  <input
v-model="reassignScope" type="radio" value="whole_case"
                    class="mt-0.5 border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span class="text-xs">
                    <span class="font-medium text-gray-800">Hand over the patient</span>
                    <span class="block text-gray-500">Transfers case ownership, so every future stage goes to them too.</span>
                  </span>
                </label>
              </div>
            </div>

            <div class="text-xs text-gray-500">
              <template v-if="!reassignScope">Choose what you are reassigning to continue.</template>
              <template v-else-if="reassignScope === 'whole_case'">
                {{ reassignTaskCount }} active stage{{ reassignTaskCount !== 1 ? 's' : '' }} and
                {{ reassignCaseCount }} case{{ reassignCaseCount !== 1 ? 's' : '' }} will move
              </template>
              <template v-else>
                {{ reassignTaskCount }} active stage{{ reassignTaskCount !== 1 ? 's' : '' }} will be reassigned
              </template>
            </div>
          </div>
          <div class="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
            <button class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800" @click="showReassignModal = false">Cancel</button>
            <button
              :disabled="!reassignMemberId || !reassignScope || !reassignTaskCount || reassigning"
              class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50 transition-colors"
              @click="executeReassign"
            >
              <FontAwesomeIcon v-if="reassigning" :icon="['fas', 'spinner']" class="animate-spin mr-1" />
              Reassign
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Footer with pagination -->
    <div class="px-4 py-2 border-t border-gray-200 bg-gray-50 shrink-0">
      <div class="flex items-center justify-between text-xs text-gray-600">
        <span>
          {{ sortedPatients.length }} patients
          <span v-if="totalPages > 1" class="text-gray-400 ml-1">
            — showing {{ currentPage * pageSize + 1 }}–{{ Math.min((currentPage + 1) * pageSize, sortedPatients.length) }}
          </span>
        </span>
        <div v-if="totalPages > 1" class="flex items-center space-x-1">
          <button
            :disabled="currentPage === 0"
            class="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-default"
            @click="currentPage = 0"
          >«</button>
          <button
            :disabled="currentPage === 0"
            class="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-default"
            @click="currentPage--"
          >‹</button>
          <span class="px-2 text-gray-700 font-medium">{{ currentPage + 1 }} / {{ totalPages }}</span>
          <button
            :disabled="currentPage >= totalPages - 1"
            class="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-default"
            @click="currentPage++"
          >›</button>
          <button
            :disabled="currentPage >= totalPages - 1"
            class="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-default"
            @click="currentPage = totalPages - 1"
          >»</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { z } from 'zod';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import RiskAcuityBadge from './Custom_Cell_Renderers/RiskAcuityBadge.vue';
import { BrowserStore } from '@/utils/BrowserStore';
import { formatDueTime } from '@/utils/common';

const colWidthsStore = new BrowserStore(
  'cm_patient_table_col_widths',
  z.object({
    patient: z.number().default(180),
    practice: z.number().default(155),
    payer: z.number().default(145),
    index_event: z.number().default(145),
    latest_activity: z.number().default(150),
    risk_acuity: z.number().default(110),
    programs: z.number().default(115),
    stage: z.number().default(160),
    due_date: z.number().default(100),
    assigned: z.number().default(110),
    work: z.number().default(90),
  }),
);


export default {
  name: 'CareManagementPatientTable',
  components: { FontAwesomeIcon, RiskAcuityBadge },
  props: {
    patients: { type: Array, default: () => [] },
    selectedEntityId: { type: Number, default: null },
    loading: { type: Boolean, default: false },

  },
  emits: ['select-patient', 'open-task', 'patients-reassigned'],
  data() {
    return {
      currentPage: 0,
      pageSize: 100,
      sortKey: null,
      sortDir: 'desc',
      selectedEntityIds: new Set(),
      showReassignModal: false,
      // Reassign scope: '' | 'stages_only' | 'whole_case'. Starts UNSET on purpose
      // — see the modal comment. Reset on every open so a prior choice never
      // silently carries into a different reassignment.
      reassignScope: '',
      reassignMetricSlug: '',
      reassignMemberId: null,
      reassignMembers: [],
      reassignStageKeys: new Set(),
      reassigning: false,
      colWidths: colWidthsStore.load(),
      baseColumns: [
        { key: 'patient', label: 'Patient' },
        { key: 'practice', label: 'Practice / Provider' },
        { key: 'payer', label: 'Payer / Contract' },
        { key: 'index_event', label: 'Index Event' },
        { key: 'latest_activity', label: 'Latest Activity' },
        { key: 'risk_acuity', label: 'Risk/Acuity' },
        { key: 'programs', label: 'Programs' },
        { key: 'stage', label: 'Stage' },
        { key: 'due_date', label: 'Due Date' },
        { key: 'assigned', label: 'Assigned To' },
        { key: 'work', label: 'Work' },
      ]
    };
  },
  computed: {
    ...mapGetters('tenant', ['opportunityNounPlural']),
    tableMinWidth() {
      return 32 + Object.values(this.colWidths).reduce((a, b) => a + b, 0);
    },
    // Column headers with the per-tenant "programs" label applied (IHCI →
    // "Opportunities"). key stays 'programs' so colWidths + sort are unaffected.
    columns() {
      return this.baseColumns.map(c => (
        c.key === 'programs' ? { ...c, label: this.opportunityNounPlural } : c
      ));
    },
    allVisibleSelected() {
      if (!this.visiblePatients.length) return false;
      return this.visiblePatients.every(p => this.selectedEntityIds.has(p.entity_id));
    },
    // Active tasks across the selected patients, optionally scoped to the chosen program.
    reassignCandidateTasks() {
      const out = [];
      for (const p of this.patients) {
        if (!this.selectedEntityIds.has(p.entity_id)) continue;
        for (const t of (p.tasks || [])) {
          if (![1, 4, 7].includes(t.status_id)) continue;
          if (this.reassignMetricSlug && t.metric_slug !== this.reassignMetricSlug) continue;
          out.push(t);
        }
      }
      return out;
    },
    // Programs present among the selected patients' active tasks (for the dropdown).
    selectedProgramOptions() {
      const m = new Map();
      for (const p of this.patients) {
        if (!this.selectedEntityIds.has(p.entity_id)) continue;
        for (const t of (p.tasks || [])) {
          if (![1, 4, 7].includes(t.status_id) || !t.metric_slug) continue;
          if (!m.has(t.metric_slug)) m.set(t.metric_slug, t.metric_name || t.metric_slug);
        }
      }
      return Array.from(m, ([slug, label]) => ({ slug, label })).sort((a, b) => a.label.localeCompare(b.label));
    },
    // Distinct stages (task types) among the candidate tasks, with counts.
    reassignStageOptions() {
      const m = new Map();
      for (const t of this.reassignCandidateTasks) {
        const key = String(t.task_type_id);
        if (!m.has(key)) {
          const label = [t.metric_name, t.task_type].filter(Boolean).join(' — ') || t.task_type || `Stage ${key}`;
          m.set(key, { key, label, count: 0 });
        }
        m.get(key).count += 1;
      }
      return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
    },
    allStagesSelected() {
      return this.reassignStageOptions.length > 0 &&
        this.reassignStageOptions.every(s => this.reassignStageKeys.has(s.key));
    },
    // Tasks that will actually be reassigned: candidate tasks whose stage is selected.
    reassignTargetTasks() {
      return this.reassignCandidateTasks.filter(t => this.reassignStageKeys.has(String(t.task_type_id)));
    },
    reassignTaskCount() {
      return this.reassignTargetTasks.length;
    },
    // Distinct opportunities behind the targeted stages — the cases that would
    // change hands under 'whole_case'. One case can carry several stages, so this
    // is deliberately not the task count.
    reassignCaseIds() {
      const s = new Set();
      for (const t of this.reassignTargetTasks) if (t.opportunity_id) s.add(t.opportunity_id);
      return Array.from(s);
    },
    reassignCaseCount() {
      return this.reassignCaseIds.length;
    },
    sortedPatients() {
      if (!this.sortKey) {
        // Default sort: priority desc → uncontacted first → overdue → name
        return [...this.patients].sort((a, b) => {
          const scoreA = a.total_priority_score || 0;
          const scoreB = b.total_priority_score || 0;
          if (scoreA !== scoreB) return scoreB - scoreA;
          // Tie on priority (e.g. tenants without a real priority signal, where
          // every score is a flat constant) → earliest due date first, no-due last.
          const dueA = this.earliestDueDate(a) || '9999-12-31';
          const dueB = this.earliestDueDate(b) || '9999-12-31';
          if (dueA !== dueB) return dueA < dueB ? -1 : 1;
          const workA = (a.work_completed || []).length;
          const workB = (b.work_completed || []).length;
          if (workA !== workB) return workA - workB;
          const overdueA = a.num_overdue_tasks || 0;
          const overdueB = b.num_overdue_tasks || 0;
          if (overdueA !== overdueB) return overdueB - overdueA;
          return (a.patient_name || '').localeCompare(b.patient_name || '');
        });
      }

      const dir = this.sortDir === 'asc' ? 1 : -1;
      const key = this.sortKey;
      const self = this;

      return [...this.patients].sort((a, b) => {
        let va, vb;
        switch (key) {
          case 'patient':
            va = a.patient_name || ''; vb = b.patient_name || '';
            return dir * va.localeCompare(vb);
          case 'practice':
            va = a.practice_name || ''; vb = b.practice_name || '';
            return dir * va.localeCompare(vb);
          case 'payer':
            va = self.patientPayer(a); vb = self.patientPayer(b);
            return dir * va.localeCompare(vb);
          case 'index_event':
            va = self.activityInfo(a).index?.raw || ''; vb = self.activityInfo(b).index?.raw || '';
            return dir * (va < vb ? -1 : va > vb ? 1 : 0);
          case 'latest_activity':
            va = self.activityInfo(a).latest?.raw || ''; vb = self.activityInfo(b).latest?.raw || '';
            return dir * (va < vb ? -1 : va > vb ? 1 : 0);
          case 'risk_acuity':
            va = a.total_priority_score || 0; vb = b.total_priority_score || 0;
            return dir * (va - vb);

            return dir * (va - vb);
          case 'programs':
            va = (a.all_programs || a.enrolled_programs || []).length;
            vb = (b.all_programs || b.enrolled_programs || []).length;
            return dir * (va - vb);
          case 'stage':
            va = (self.groupedTasks(a)[0] || {}).task_type || '';
            vb = (self.groupedTasks(b)[0] || {}).task_type || '';
            return dir * va.localeCompare(vb);
          case 'due_date':
            va = this.earliestDueDate(a) || '9999-12-31'; vb = this.earliestDueDate(b) || '9999-12-31';
            return dir * va.localeCompare(vb);
          case 'assigned':
            va = self.patientOwners(a).length; vb = self.patientOwners(b).length;
            return dir * (va - vb);
          case 'work':
            va = (a.work_completed || []).length; vb = (b.work_completed || []).length;
            return dir * (va - vb);
          default:
            return 0;
        }
      });
    },
    totalPages() {
      return Math.ceil(this.sortedPatients.length / this.pageSize);
    },
    visiblePatients() {
      const start = this.currentPage * this.pageSize;
      return this.sortedPatients.slice(start, start + this.pageSize);
    }
  },
  watch: {
    selectedEntityId(newId) {
      if (!newId) return;
      const globalIdx = this.sortedPatients.findIndex(p => p.entity_id === newId);
      if (globalIdx === -1) return;
      this.currentPage = Math.floor(globalIdx / this.pageSize);
      this._scrollToSelected();
    },
    patients() {
      // When the patient list refreshes (e.g. FilterBar triggers a second loadPatients
      // after the deep-link patient was already selected), don't blindly reset to page 0.
      // If a patient is selected and still in the list, jump to their page and re-scroll.
      if (this.selectedEntityId) {
        const globalIdx = this.sortedPatients.findIndex(p => p.entity_id === this.selectedEntityId);
        if (globalIdx >= 0) {
          this.currentPage = Math.floor(globalIdx / this.pageSize);
          this._scrollToSelected();
          return;
        }
      }
      this.currentPage = 0;
    },
    showReassignModal(val) {
      if (val) this.loadReassignMembers();
    },
    // When the program filter changes, default to all stages in the new scope.
    reassignMetricSlug() {
      this.selectAllStages();
    },
  },

  methods: {
    formatDueTime,
    toggleSelect(entityId) {
      const next = new Set(this.selectedEntityIds);
      if (next.has(entityId)) next.delete(entityId);
      else next.add(entityId);
      this.selectedEntityIds = next;
    },
    // Called by watchers and by the parent via $refs after deep-link navigation.
    // Uses nextTick → setTimeout(0) so the scroll happens after:
    //   1. Vue flushes currentPage change and re-renders visiblePatients ($nextTick)
    //   2. All remaining microtasks from that flush settle (setTimeout 0)
    _scrollToSelected() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.$el?.querySelector('tr.bg-blue-50')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 0);
      });
    },
    // Public: parent calls this via $refs after setting selectedPatient
    scrollToSelected() {
      if (!this.selectedEntityId) return;
      const globalIdx = this.sortedPatients.findIndex(p => p.entity_id === this.selectedEntityId);
      if (globalIdx < 0) return;
      this.currentPage = Math.floor(globalIdx / this.pageSize);
      this._scrollToSelected();
    },
    toggleSelectAll() {
      if (this.allVisibleSelected) {
        const next = new Set(this.selectedEntityIds);
        for (const p of this.visiblePatients) next.delete(p.entity_id);
        this.selectedEntityIds = next;
      } else {
        const next = new Set(this.selectedEntityIds);
        for (const p of this.visiblePatients) next.add(p.entity_id);
        this.selectedEntityIds = next;
      }
    },
    clearSelection() {
      this.selectedEntityIds = new Set();
    },
    openReassignModal() {
      this.reassignMetricSlug = '';
      this.reassignMemberId = null;
      this.reassignScope = '';
      this.selectAllStages();
      this.showReassignModal = true;
    },
    selectAllStages() {
      this.reassignStageKeys = new Set(this.reassignStageOptions.map(s => s.key));
    },
    toggleStage(key) {
      const next = new Set(this.reassignStageKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      this.reassignStageKeys = next;
    },
    toggleAllStages() {
      if (this.allStagesSelected) this.reassignStageKeys = new Set();
      else this.selectAllStages();
    },
    async loadReassignMembers() {
      try {
        const { data } = await this.$axios.get('/api/team-assignments/summary/');
        const rows = Array.isArray(data) ? data : (data.members || []);
        this.reassignMembers = rows.map(m => ({ id: m.member_id, name: m.member__name || m.member_name }));
      } catch (e) {
        console.error('Error loading members:', e);
      }
    },
    async executeReassign() {
      // Both a member AND an explicit scope are required — no default scope.
      if (!this.reassignMemberId || !this.reassignScope) return;
      // Stage-aware: only the active tasks whose stage (task type) is checked.
      const taskIds = this.reassignTargetTasks.map(t => t.id);
      if (!taskIds.length) {
        this.$root.$emit('show-error-toast', 'No active tasks match the selected program and stages');
        return;
      }
      this.reassigning = true;
      try {
        // 'whole_case' ALSO sends opportunity_ids, which is what transfers durable
        // case ownership so future stages follow the new person. Without it the
        // reassignment covers these stages only and the next one returns to the
        // existing owner — correct for a cover, wrong for a hand-over.
        const payload = { task_ids: taskIds, member_id: this.reassignMemberId };
        if (this.reassignScope === 'whole_case') payload.opportunity_ids = this.reassignCaseIds;
        const { data } = await this.$axios.post('/api/team-assignments/bulk-assign/', payload);
        const nT = data?.tasks ?? taskIds.length;
        const nC = data?.cases ?? 0;
        this.$root.$emit('show-success-toast',
          this.reassignScope === 'whole_case'
            ? `Reassigned ${nT} stage${nT !== 1 ? 's' : ''} and handed over ${nC} case${nC !== 1 ? 's' : ''}`
            : `Reassigned ${nT} stage${nT !== 1 ? 's' : ''}`);
        this.showReassignModal = false;
        this.clearSelection();
        this.$emit('patients-reassigned');
      } catch (e) {
        console.error('Error reassigning:', e);
        this.$root.$emit('show-error-toast', 'Failed to reassign');
      } finally {
        this.reassigning = false;
      }
    },

    startColResize(e, colKey) {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = this.colWidths[colKey];
      const onMove = (ev) => {
        this.colWidths[colKey] = Math.max(60, startWidth + (ev.clientX - startX));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        colWidthsStore.save(this.colWidths);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        const defaultDir = key === 'patient' || key === 'practice' || key === 'payer' ? 'asc' : 'desc';
        if (this.sortDir === defaultDir) {
          // click 2: reverse
          this.sortDir = defaultDir === 'asc' ? 'desc' : 'asc';
        } else {
          // click 3: reset
          this.sortKey = null;
          this.sortDir = 'desc';
        }
      } else {
        this.sortKey = key;
        this.sortDir = key === 'patient' || key === 'practice' || key === 'payer' || key === 'due_date' ? 'asc' : 'desc';
      }
      this.currentPage = 0;
    },
    workIcon(typeName) {
      const map = {
        'clinical_engagement': ['fas', 'phone'],
        'phone_call': ['fas', 'phone'],
        'care_gap_patient_call': ['fas', 'phone'],
        'PHQ9': ['fas', 'clipboard-list'],
        'GAD7': ['fas', 'clipboard-list'],
        'comment': ['fas', 'comment'],
        'patient_call': ['fas', 'phone'],
      };
      return map[typeName] || ['fas', 'check'];
    },
    workDotColor(typeName) {
      const map = {
        'clinical_engagement': 'bg-primary-500',
        'care_gap_patient_call': 'bg-blue-500',
        'phone_call': 'bg-green-500',
        'PHQ9': 'bg-purple-500',
        'GAD7': 'bg-purple-500',
        'comment': 'bg-gray-400',
        'patient_call': 'bg-green-500',
      };
      return map[typeName] || 'bg-gray-500';
    },
    workTooltip(work) {
      const name = work.work_type_display_name || work.work_type_name || 'Work';
      const date = work.created_at ? new Date(work.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      const user = work.appuser_name || '';
      return [name, user, date].filter(Boolean).join(' · ');
    },
    patientOwners(patient) {
      // Each task carries at most one resolved owner (latest assignment row);
      // dedupe across the patient's tasks by member id.
      const seen = {};
      for (const t of (patient.tasks || [])) {
        for (const o of (t.owners || [])) {
          if (o && o.id && !seen[o.id]) seen[o.id] = o;
        }
      }
      return Object.values(seen);
    },
    ownerBadgeColor(role) {
      const map = {
        'Primary Owner': 'bg-primary-100 text-primary-700',
        'RN':            'bg-rose-100 text-rose-700',
        'Health Coach':  'bg-green-100 text-green-700',
        'PharmD':        'bg-purple-100 text-purple-700',
        'LCSW':          'bg-amber-100 text-amber-700',
        'MA':            'bg-cyan-100 text-cyan-700',
        'Supervisor':    'bg-indigo-100 text-indigo-700',
        'Consultant':    'bg-gray-100 text-gray-600',
      };
      return map[role] || 'bg-gray-100 text-gray-600';
    },
    ownerInitials(name) {
      if (!name) return '?';
      const parts = name.split(/[\s,]+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return parts[0].substring(0, 2).toUpperCase();
    },
    earliestDueDate(patient) {
      // Only ACTIVE tasks (status 1 planned / 4 in-progress / 7) carry a live due
      // date. Completed/closed tasks (status 2/3) must NOT surface their stale due
      // date, or a fully-worked patient shows a misleading red "Overdue" (req 1873).
      return (patient.tasks || [])
        .filter(t => [1, 4, 7].includes(t.status_id))
        .map(t => t.due_date)
        .filter(Boolean)
        .sort()[0] || null;
    },
    earliestDueTime(patient) {
      const tasks = (patient.tasks || [])
        .filter(t => [1, 4, 7].includes(t.status_id) && t.due_date)
        .sort((a, b) => a.due_date.localeCompare(b.due_date));
      return tasks.length ? tasks[0].due_time || null : null;
    },
    groupedTasks(patient) {
      const tasks = patient.tasks || [];
      const map = {};
      for (const t of tasks) {
        if (!map[t.id]) {
          map[t.id] = { ...t, opportunity_ids: [] };
        }
        map[t.id].opportunity_ids.push(t.opportunity_id);
      }
      return Object.values(map);
    },
    stageLabel(group) {
      const tt = group.task_type || '';
      const attempt = (group.call_attempts || 0) + 1;
      return `${tt} (${attempt})`;
    },
    // Outreach-attempt status ramp (call_attempts is the clinical-engagement
    // count derived per task in the queue; matches the (N) in the stage label):
    //   0        not started   hollow gray
    //   1-2      in progress   yellow
    //   >=3      stalling      red
    statusDotClass(group) {
      // Terminal task (Closed Out / Complete) reads as done — green — regardless
      // of how many call attempts it took. Only OPEN tasks use the attempt ramp.
      if ([2, 3].includes(group.status_id)) return 'bg-green-500 border-green-500';
      const n = group.call_attempts || 0;
      if (n >= 3) return 'bg-red-500 border-red-500';
      if (n >= 1) return 'bg-yellow-400 border-yellow-400';
      return 'bg-transparent border-gray-400';
    },
    statusLabel(group) {
      if ([2, 3].includes(group.status_id)) return 'Completed';
      const n = group.call_attempts || 0;
      if (n >= 3) return 'Stalling';
      if (n >= 1) return 'In progress';
      return 'Not started';
    },
    launchClinicalEngagement(group) {
      // Open a new clinical engagement for this task in a new tab.
      // Mirrors Patient_Task.openClinicalEngagement: needs an opportunity id
      // and the task id. The engagement page resolves/creates the work item.
      const oppId = (group.opportunity_ids || [])[0];
      if (oppId) {
        window.open(`/work/clinicalengagement/new/${oppId}/${group.id}`, '_blank');
      } else {
        this.$root.$emit('show-error-toast', 'No opportunity linked to this task');
      }
    },
    stageStyle(group, patient) {
      const progs = this.taskPrograms(group, patient);
      const color = progs[0]?.uicolor || '#6B7280';
      return {
        backgroundColor: color + '12',
        color: color,
        borderColor: color + '35',
      };
    },
    taskPrograms(group, patient) {
      const progs = patient.enrolled_programs || [];
      const oppIds = group.opportunity_ids || [];
      return progs.filter(p => oppIds.includes(p.id));
    },
    programForTask(oppId, patient) {
      return (patient.enrolled_programs || []).find(p => p.id === oppId);
    },
    programName(oppId, patient) {
      const prog = this.programForTask(oppId, patient);
      return prog ? prog.name : '';
    },
    programStyle(oppId, patient) {
      const prog = this.programForTask(oppId, patient);
      const color = prog?.uicolor || '#10b981';
      return {
        backgroundColor: color + '15',
        color: color,
        borderColor: color + '40'
      };
    },
    patientPayer(patient) {
      const progs = patient.enrolled_programs || [];
      for (const p of progs) {
        if (p.payer_name) return p.payer_name;
      }
      return '—';
    },
    patientContract(patient) {
      const progs = patient.enrolled_programs || [];
      for (const p of progs) {
        if (p.contract_name) return p.contract_name;
      }
      return null;
    },
    titleCase(str) {
      if (!str) return str;
      const keep = new Set(['LLC', 'PLLC', 'PC', 'MD', 'DO', 'NP', 'PA', 'RN', 'DPM', 'OD', 'II', 'III', 'IV']);
      return str.split(/\s+/).map(w => {
        const clean = w.replace(/[,.]$/, '');
        if (keep.has(clean.toUpperCase())) return w.toUpperCase();
        return w.toLowerCase().replace(/^\w/, c => c.toUpperCase());
      }).join(' ');
    },
    programAbbrev(name) {
      if (!name) return '?';
      if (name.includes('VytalCare') && name.includes('TOC')) return 'VC+ TOC';
      if (name.includes('Transitions')) return 'TOC';
      if (name.includes('VytalCare') || name.includes('Vytal Care')) return 'VC+';
      if (name.includes('Chronic Disease')) return 'CDM';
      if (name.includes('SNF')) return 'SNF';
      if (name.includes('Direct Referral')) return 'REF';
      // Generic: first letter of each word
      return name.split(/\s+/).slice(0, 3).map(w => w[0]).join('').toUpperCase();
    },
    activityInfo(patient) {
      const STANDARD_CODE_LABELS = {
        '1': 'Home/Self Care', '3': 'SNF', '6': 'Home Health',
        '8': 'Home IV', '20': 'Expired', '40': 'Hospice - Home', '41': 'Hospice - Facility'
      };
      // Index discharge: TOC trigger event
      const progs = patient.enrolled_programs || [];
      let index = null;
      for (const p of progs) {
        if (p.discharge_time && (!index || p.discharge_time > index.raw)) {
          const ep = (p.episode_type || '').toUpperCase();
          const type = ep.includes('ED') ? 'ED' : ep.includes('OBS') ? 'OBS' : ep.includes('INP') ? 'IP' : ep.substring(0, 3);
          // Disposition: prefer standard code label, fall back to raw
          const stdCode = p.discharge_standard_code;
          const stdLabel = stdCode ? STANDARD_CODE_LABELS[String(stdCode)] || null : null;
          let rawDispo = p.raw_disposition || p.disposition_category || null;
          if (rawDispo) rawDispo = String(rawDispo).replace(/^[A-Z]+\^/, '').trim();
          index = {
            raw: p.discharge_time,
            type,
            date: this.shortDate(p.discharge_time),
            ago: this.ago(p.discharge_time),
            disposition: stdLabel || rawDispo,
            dispositionFull: rawDispo,
            facility: p.facility || null,
          };
        }
      }
      // Last ADT activity — pick the most recent across all programs
      let latest = null;
      for (const p of progs) {
        const t = p.last_adt_event_time;
        if (t && (!latest || t > latest.raw)) {
          const stdCode = p.last_adt_discharge_standard_code;
          const stdLabel = stdCode ? STANDARD_CODE_LABELS[String(stdCode)] || null : null;
          let rawDispo = p.last_adt_raw_disposition || p.last_adt_disposition_category || null;
          if (rawDispo) rawDispo = String(rawDispo).replace(/^[A-Z]+\^/, '').trim();
          latest = {
            raw: t,
            type: p.last_adt_event_type || '—',
            direction: p.last_adt_event_direction || '',
            date: this.shortDate(t),
            ago: this.ago(t),
            disposition: stdLabel || rawDispo,
            dispositionFull: rawDispo,
            facility: p.last_adt_facility || p.facility || null,
          };
        }
      }
      return { index, latest };
    },
    activitySortKey(patient) {
      const info = this.activityInfo(patient);
      const idx = info.index?.raw || '';
      const lat = info.latest?.raw || '';
      return lat > idx ? lat : idx;
    },
    episodeTypeColor(type) {
      if (type === 'IP') return 'text-violet-600';
      if (type === 'OBS') return 'text-amber-600';
      if (type === 'ED') return 'text-rose-600';
      return 'text-sky-600';
    },
    // ADT/discharge timestamps (discharge_time, last_adt_event_time) arrive as
    // timezone-NAIVE strings holding the facility-local wall-clock — the HL7
    // ingestor strips the zone offset and stores the raw digits (TIMESTAMP_NTZ).
    // `new Date("2026-06-25 21:18:00.000")` would apply the BROWSER's offset and
    // shift the calendar day (an evening event rolling to "tomorrow"), which is
    // the "Index Event shows today when it happened yesterday" bug. Parse the
    // components literally so the day shown is exactly the stored wall-clock day,
    // with no timezone shift.
    parseAdtTime(iso) {
      if (!iso) return null;
      const m = String(iso).match(/(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
      if (!m) return null;
      return new Date(
        Number(m[1]), Number(m[2]) - 1, Number(m[3]),
        Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0),
      );
    },
    shortDate(iso) {
      const d = this.parseAdtTime(iso);
      if (!d) return '';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    ago(iso) {
      const d = this.parseAdtTime(iso);
      if (!d) return '';
      // Compare wall-clock day to today's wall-clock day (both local-frame).
      const startOfDay = (dt) => { const x = new Date(dt); x.setHours(0, 0, 0, 0); return x; };
      const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
      if (days < 0) return 'future';
      if (days === 0) return 'today';
      if (days === 1) return '1d ago';
      if (days < 30) return `${days}d ago`;
      const months = Math.floor(days / 30);
      return months === 1 ? '1mo ago' : `${months}mo ago`;
    },
    formatDob(dob) {
      if (!dob) return '';
      // Tolerate any date-string separator: 'YYYY-MM-DD', 'YYYY-MM-DD HH:MM:SS'
      // (sync/migration), or 'YYYY-MM-DDTHH:MM:SS' (manual care_management_ui
      // enroll). Take the first 10 chars and build from parts — same robust
      // approach as the drawer's Patient_Detail_Info.formatDate.
      const parts = String(dob).substring(0, 10).split('-');
      if (parts.length !== 3) return '';
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    },

    dueDateClass(task) {
      if (!task.due_date) return 'text-gray-400';
      const d = new Date(task.due_date + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diff = Math.ceil((d - today) / 86400000);
      if (diff < 0) return 'text-red-600 font-semibold';
      if (diff === 0) return 'text-yellow-600 font-semibold';
      if (diff <= 7) return 'text-gray-700';
      return 'text-gray-500';
    },
    formatDueLabel(task) {
      if (!task.due_date) return '';
      const d = new Date(task.due_date + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diff = Math.ceil((d - today) / 86400000);
      if (diff < 0) return `Overdue`;
      if (diff === 0) return 'Due today';
      if (diff === 1) return 'Tomorrow';
      if (diff <= 7) return `${diff} days`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }
};
</script>

<style scoped>
td {
  overflow: hidden;
}
</style>
